import * as React from "react";
import styled from "styled-components";

import Modal from "@/components/Modal";
import { useSaveContext } from "@/queries/useTranslations";
import { CompactActionButton } from "@/styles/stylings";
import type { IContentType } from "@/types";

const ContextDisplay = styled.div`
    margin-bottom: 2px;
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
    flex-wrap: wrap;
`;

const ContextLabel = styled.span`
    font-weight: 600;
`;

const ContextText = styled.span`
    white-space: pre-wrap;
`;

const ContextActions = styled.div`
    display: flex;
    gap: 0.25rem;
`;

const ContextItemId = styled.p`
    margin-top: 0;
`;

const ContextTextarea = styled.textarea`
    width: 100%;
    min-height: 10rem;
    resize: vertical;
    box-sizing: border-box;
    margin-bottom: 0.5rem;
`;

interface IProps {
    version: string;
    id: string;
    contentType: IContentType;
    context: string;
}

function ContextEditor({ version, id, contentType, context }: IProps) {
    const [isOpen, setIsOpen] = React.useState(false);
    const [draft, setDraft] = React.useState(context);
    const { mutate, isPending } = useSaveContext();

    React.useEffect(() => {
        if (!isOpen) {
            setDraft(context);
        }
    }, [context, isOpen]);

    const open = React.useCallback(() => {
        setDraft(context);
        setIsOpen(true);
    }, [context]);

    const cancel = React.useCallback(() => {
        if (isPending) {
            return;
        }
        setDraft(context);
        setIsOpen(false);
    }, [context, isPending]);

    const save = React.useCallback(() => {
        mutate(
            {
                version,
                contentType,
                id,
                context: draft,
            },
            {
                onSuccess: () => setIsOpen(false),
            },
        );
    }, [contentType, draft, id, mutate, version]);

    return (
        <>
            <ContextDisplay>
                <ContextLabel>Context:</ContextLabel>
                {context && <ContextText>{context}</ContextText>}
                <CompactActionButton type="button" onClick={open}>
                    {context ? "Edit context" : "Add context"}
                </CompactActionButton>
            </ContextDisplay>
            <Modal
                isOpen={isOpen}
                onRequestClose={cancel}
                contentLabel={context ? "Edit context" : "Add context"}
                shouldCloseOnOverlayClick={!isPending}
                shouldCloseOnEsc={!isPending}
            >
                <h2>Edit context</h2>
                <ContextItemId>
                    <strong>ID:</strong> {id}
                </ContextItemId>
                <ContextTextarea
                    aria-label={`Context for ${id}`}
                    value={draft}
                    onChange={(event) => setDraft(event.currentTarget.value)}
                    disabled={isPending}
                />
                <ContextActions>
                    <button type="button" onClick={save} disabled={isPending}>
                        Save context
                    </button>
                    <button type="button" onClick={cancel} disabled={isPending}>
                        Cancel
                    </button>
                </ContextActions>
            </Modal>
        </>
    );
}

export default React.memo(ContextEditor);
